import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import { purchasesApi } from "../services/api";
import { usePermissions } from "../hooks/usePermissions";
import type { PurchaseItemType, UserPurchase } from "../types";
import { PurchaseSpendingCard } from "../components/PurchaseSpendingCard";
import {
  GAME_FORMAT_LABELS,
  PURCHASE_TYPE_LABELS,
  isPurchaseItemType,
} from "./purchaseTypes";

const { Title } = Typography;
const { Search } = Input;

export function PurchasesListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canDelete } = usePermissions();

  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const [search, setSearch] = useState("");
  // Type filter lives in the URL so the "Gry" menu link can preselect it
  const [searchParams, setSearchParams] = useSearchParams();
  const rawType = searchParams.get("item_type");
  const itemType: PurchaseItemType | undefined = isPurchaseItemType(rawType)
    ? rawType
    : undefined;
  const setItemType = (v: PurchaseItemType | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("item_type", v);
    else next.delete("item_type");
    setSearchParams(next);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["purchases", pagination, search, itemType],
    queryFn: () =>
      purchasesApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
        search: search || undefined,
        item_type: itemType,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: purchasesApi.delete,
    onSuccess: () => {
      message.success("Zakup usunięty pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const typeOptions = useMemo(
    () =>
      (Object.keys(PURCHASE_TYPE_LABELS) as PurchaseItemType[]).map((t) => ({
        value: t,
        label: PURCHASE_TYPE_LABELS[t].plural,
      })),
    [],
  );

  const columns: ColumnsType<UserPurchase> = [
    {
      title: "Data",
      dataIndex: "purchased_at",
      key: "purchased_at",
      width: 120,
      render: (d: string) => new Date(d).toLocaleDateString("pl-PL"),
    },
    {
      title: "Typ",
      dataIndex: "item_type",
      key: "item_type",
      width: 120,
      render: (t: PurchaseItemType) => (
        <Tag color={PURCHASE_TYPE_LABELS[t]?.color}>
          {PURCHASE_TYPE_LABELS[t]?.singular ?? t}
        </Tag>
      ),
    },
    {
      title: "Nazwa",
      key: "name",
      render: (_: unknown, record: UserPurchase) => {
        if (record.item_type === "watch") {
          const brand = record.watch_details?.brand?.name || "";
          const model = record.watch_details?.model || "";
          const ref = record.watch_details?.reference_number
            ? ` (${record.watch_details.reference_number})`
            : "";
          const text = `${brand} ${model}`.trim();
          return (
            <strong>
              {text || "-"}
              {ref}
            </strong>
          );
        }
        if (record.item_type === "game") {
          const g = record.game_details;
          const extras = [
            g?.platform,
            g?.format ? GAME_FORMAT_LABELS[g.format] : null,
            g?.edition,
          ].filter(Boolean);
          return (
            <span>
              <strong>{g?.title || "-"}</strong>
              {g?.is_dlc && (
                <Tag style={{ marginLeft: 8 }} color="gold">
                  DLC
                </Tag>
              )}
              {extras.length > 0 && (
                <span style={{ color: "#888", marginLeft: 8 }}>
                  {extras.join(" · ")}
                </span>
              )}
            </span>
          );
        }
        const house = record.perfume_details?.brand?.name || "";
        const name = record.perfume_details?.fragrance_name || "";
        const text = `${house} ${name}`.trim();
        return <strong>{text || "-"}</strong>;
      },
    },
    {
      title: "Cena",
      dataIndex: "price",
      key: "price",
      width: 140,
      render: (p: number | null | undefined, record: UserPurchase) => {
        if (p === null || p === undefined) return "-";
        return `${p} ${record.currency_code || ""}`.trim();
      },
    },
    {
      title: "Sklep",
      dataIndex: "vendor_name",
      key: "vendor_name",
      responsive: ["lg"],
      render: (v: string | null | undefined) => v || "-",
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: UserPurchase) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/purchases/${record.id}/edit`)}
          />
          <Popconfirm
            title="Usunąć ten zakup?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>
            {itemType === "game" ? "Gry" : "Zakupy"}
          </Title>
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Search
              placeholder="Szukaj (sklep, numer zamówienia, notatki, marka/model, zapach, tytuł gry...)"
              allowClear
              onSearch={(v) => {
                setSearch(v);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
              style={{ width: 420 }}
            />
            <Select
              placeholder="Typ"
              allowClear
              options={typeOptions}
              value={itemType}
              style={{ width: 180 }}
              onChange={(v) => {
                setItemType(v ?? undefined);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            />
          </div>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() =>
            navigate(
              itemType
                ? `/purchases/new?item_type=${itemType}`
                : "/purchases/new",
            )
          }
        >
          Dodaj
        </Button>
      </div>

      <PurchaseSpendingCard itemType={itemType} />

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          pagination={{
            current: data?.page || pagination.current,
            pageSize: data?.page_size || pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
}

export default PurchasesListPage;
